import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { seo } from "@/lib/seo";
import { supabase } from "@/integrations/supabase/client";
import { CalendarCheck } from "lucide-react";
import {
  ALLOW_ON_DOMAIN_BOOKING,
  APPROVED_CONSULT_URL,
  CONSULT_URL_IS_EXTERNAL,
  CONSULT_CTA_LABEL,
} from "@/config/compliance";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { ConsultationRequestForm } from "@/components/book/ConsultationRequestForm";
import { SlotPicker } from "@/components/book/SlotPicker";

export const Route = createFileRoute("/book")({
  head: () =>
    seo({
      title: "Book a Consultation · Currency Thoughts",
      description:
        "Reserve a private, educational consultation with a Currency Thoughts Strategist. No pitch — a conversation about your family's plan.",
      canonical: "/book",
    }),
  component: BookPage,
});

function BookPage() {
  if (!ALLOW_ON_DOMAIN_BOOKING) return <OffDomainConsult />;
  return <OnDomainBookPage />;
}

/**
 * Below `education_plus_onsite_consult` no consultation form or scheduler may
 * run on this domain. The page states that plainly and links out.
 */
function OffDomainConsult() {
  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        <section className="bg-navy-900 text-cream-50 py-24">
          <div className="mx-auto max-w-2xl px-6 text-center">
            <Eyebrow color="bronze" align="center">Consultation</Eyebrow>
            <h1 className="mt-6 font-display font-bold text-cream-50" style={{ fontSize: "44px", lineHeight: 1.15 }}>
              Requests are handled off this site.
            </h1>
            <p className="mx-auto mt-6 max-w-[520px] font-sans text-[16px] leading-relaxed text-cream-50/85">
              This site is educational. Consultation requests are taken through our
              approved scheduling destination.
            </p>
            <div className="mt-10">
              <PrimaryButton size="lg" asChild>
                <a href={APPROVED_CONSULT_URL}
              {...(CONSULT_URL_IS_EXTERNAL ? { target: "_blank", rel: "noopener noreferrer" } : {})}>
                  {CONSULT_CTA_LABEL}
                </a>
              </PrimaryButton>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function OnDomainBookPage() {
  const [reviewContext, setReviewContext] = useState<{
    type: string;
    date: string | null;
  } | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const ctx = params.get("context");
    const id = params.get("milestone_id");
    if (ctx !== "policy_review" || !id) return;
    (async () => {
      const { data } = await supabase
        .from("policy_review_milestones")
        .select("milestone_type, milestone_date")
        .eq("id", id)
        .maybeSingle();
      if (data) {
        setReviewContext({
          type: data.milestone_type as string,
          date: data.milestone_date as string,
        });
      } else {
        setReviewContext({ type: "policy_review", date: null });
      }
    })();
  }, []);

  const LABELS: Record<string, string> = {
    "6_month": "6-Month Check-In",
    "12_month": "12-Month Review",
    "24_month": "24-Month Review",
    annual: "Annual Review",
  };

  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1 pt-20">
        {reviewContext && (
          <section className="bg-bronze-600/10 border-y border-bronze-600/30 py-4">
            <div className="mx-auto max-w-3xl px-6 flex items-center gap-4">
              <CalendarCheck size={20} className="text-bronze-600 shrink-0" />
              <div className="flex-1">
                <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-bronze-600">
                  Policy Review
                </p>
                <p className="font-sans text-[14px] text-navy-800">
                  You're scheduling your{" "}
                  <span className="font-semibold">
                    {LABELS[reviewContext.type] ?? "Policy Review"}
                  </span>
                  {reviewContext.date
                    ? ` (recommended by ${new Date(reviewContext.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })})`
                    : ""}
                  . Pick a time below.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* SECTION 1 — Hero */}
        <section className="bg-navy-900 text-cream-50 py-20">
          <div className="mx-auto max-w-3xl px-6 text-center">
            <Eyebrow color="bronze" align="center">Schedule Your Consultation</Eyebrow>
            <h1
              className="mt-6 font-display font-bold text-cream-50"
              style={{ fontSize: "48px", lineHeight: 1.15, letterSpacing: "-0.01em" }}
            >
              Let's build your family's{" "}
              <em className="text-bronze-400 italic">blueprint.</em>
            </h1>
            <p
              className="mx-auto mt-6 font-sans text-cream-50/85 leading-relaxed"
              style={{ maxWidth: "540px", fontSize: "16px" }}
            >
              Pick a time that works. 30 minutes. No cost. No pressure. Just a conversation about what matters most to your family.
            </p>
          </div>
        </section>

        {/* SECTION 2 — Consultation request */}
        <section className="bg-cream-50 pt-16">
          <div className="mx-auto px-6" style={{ maxWidth: "800px" }}>
            <ConsultationRequestForm />
          </div>
        </section>

        {/* SECTION 3 — Native scheduler (writes straight to appointments) */}
        <section className="bg-cream-50 py-16">
          <div className="mx-auto px-6" style={{ maxWidth: "800px" }}>
            <div className="text-center">
              <Eyebrow color="bronze" align="center">Pick Your Time</Eyebrow>
            </div>
            <div className="mt-8">
              <SlotPicker />
            </div>
          </div>
        </section>

        {/* SECTION 3 — What to Expect */}
        <section className="bg-cream-50 py-16">
          <div className="mx-auto max-w-6xl px-6">
            <div className="text-center">
              <Eyebrow color="bronze" align="center">What to Expect</Eyebrow>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-10">
              {EXPECT.map((step) => (
                <div key={step.n} className="flex flex-col items-center text-center">
                  <NumberSeal n={step.n} />
                  <h3
                    className="mt-6 font-display font-semibold text-navy-800"
                    style={{ fontSize: "22px" }}
                  >
                    {step.title}
                  </h3>
                  <p
                    className="mt-3 font-sans text-charcoal-500 leading-relaxed"
                    style={{ fontSize: "14px", maxWidth: "280px" }}
                  >
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4 — Trust strip */}
        <section className="bg-navy-800 py-8 text-center">
          <p
            className="font-sans font-semibold uppercase text-cream-50 flex items-center justify-center flex-wrap gap-x-3 gap-y-2"
            style={{ fontSize: "11px", letterSpacing: "0.15em" }}
          >
            <span>30-Min Call</span>
            <Dot />
            <span>Free of Charge</span>
            <Dot />
            <span>No Pressure</span>
            <Dot />
            <span>Veteran-Owned</span>
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}

const EXPECT = [
  { n: 1, title: "We Listen", body: "We start by understanding your family's story and goals." },
  { n: 2, title: "We Educate", body: "We walk you through your options in plain language." },
  { n: 3, title: "You Decide", body: "You choose what fits — never any pressure." },
] as const;

function NumberSeal({ n }: { n: number }) {
  return (
    <div className="relative flex items-center justify-center" style={{ width: 96, height: 96 }}>
      <div
        className="absolute inset-0 rounded-full border border-bronze-600/60"
        style={{ borderStyle: "dashed" }}
        aria-hidden
      />
      <div className="flex items-center justify-center rounded-full bg-navy-800 border-[3px] border-bronze-600"
        style={{ width: 80, height: 80 }}
      >
        <span
          className="font-display font-bold text-cream-50"
          style={{ fontSize: "30px" }}
        >
          {n}
        </span>
      </div>
    </div>
  );
}

function Dot() {
  return (
    <span
      className="inline-block rounded-full bg-bronze-400"
      style={{ width: 4, height: 4 }}
      aria-hidden
    />
  );
}